import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { UploadIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useUpdateProfileUser } from "@/hooks/mutations/useUpdateProfileUser";
import { files } from "@/lib/files";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { misc } from "@/lib/misc";
import { resumeFileSchema } from "@/lib/schemas";
import { novaClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";

const uploadResumeFileSchema = z.object({
  isPrimary: z.boolean(),
  resumeFile: resumeFileSchema,
  title: z.string().optional(),
});

const UploadResumeFileDialog = ({
  onOpenChange,
  onSuccess,
  open,
  userId,
}: {
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  open: boolean;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Create
  const { mutateAsync: createResumeFile, isPending: isCreatePending } =
    novaClient.internal.user.resume.active.create.useMutation();
  // Update
  const { mutateAsync: updateUser } = useUpdateProfileUser();

  /* React Hooks */
  const form = useForm<z.infer<typeof uploadResumeFileSchema>>({
    resolver: zodResolver(uploadResumeFileSchema),
    defaultValues: {
      isPrimary: true,
      title: "",
    },
  });

  /* Handlers */
  const handleSuccess = () => {
    onSuccess();
    onOpenChange(false);
    form.reset();
  };

  const handleSubmit = async (data: z.infer<typeof uploadResumeFileSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Upload resume",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      { has_title: !!data.title, is_primary: data.isPrimary },
    );
    const fileBase64 = await misc.convertFileToBase64(data.resumeFile);
    const resumeFile = await createResumeFile({
      fileBase64: fileBase64 as string,
      fileName: data.resumeFile.name,
      // Prefer `||` because `data.title` could be the empty string.
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      title: data.title || files.formatDefaultResumeTitle(data.resumeFile),
      userId,
    });
    if (data.isPrimary) {
      await updateUser({
        data: { primaryResumeId: resumeFile.id },
        userId,
      });
    }
    handleSuccess();
  };

  /* Render */
  const render = () => {
    return (
      <Dialog
        onOpenChange={(isOpen) => {
          form.reset();
          onOpenChange(isOpen);
        }}
        open={open}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-slate-700">
              Upload Resume
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              className="flex flex-col gap-6"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Title (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="resumeFile"
                render={({ field: { onChange, ...field } }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Resume</FormLabel>
                    <FormControl>
                      <Input
                        accept=".doc,.docx,.pdf"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          onChange(file ?? undefined);
                        }}
                        type="file"
                        {...field}
                        value={undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isPrimary"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        id="isPrimary"
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 text-slate-700">
                      Set as Primary Resume
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Row className="justify-end">
                <Button
                  className="rounded-full"
                  disabled={isCreatePending}
                  type="submit"
                >
                  Upload
                </Button>
              </Row>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};

export const UploadResumeDialog = ({
  buttonClassName,
  isIconOnly = false,
  onUploaded,
  userId,
  variant = "outline",
}: {
  buttonClassName?: string;
  isIconOnly?: boolean;
  onUploaded: () => void;
  userId: number;
  variant?: "outline" | "default" | "secondary";
}) => {
  /* React Hooks */
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  /* Handlers */
  const handleOpen = () => {
    setIsDialogOpen(true);
  };

  /* Render */
  const renderTrigger = () => {
    if (isIconOnly) {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={cn(
                  "h-6 w-6 shrink-0 text-slate-400 hover:text-slate-600",
                  buttonClassName,
                )}
                onClick={handleOpen}
                size="icon"
                type="button"
                variant="ghost"
              >
                <UploadIcon className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <Text className="text-xs font-normal">
                Upload a resume to this candidate&apos;s profile
              </Text>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <Button
        className={buttonClassName ?? "rounded-full"}
        onClick={handleOpen}
        type="button"
        variant={variant}
      >
        <UploadIcon className="h-4 w-4" />
        Upload Resume
      </Button>
    );
  };

  const render = () => {
    return (
      <>
        {renderTrigger()}
        <UploadResumeFileDialog
          onOpenChange={setIsDialogOpen}
          onSuccess={onUploaded}
          open={isDialogOpen}
          userId={userId}
        />
      </>
    );
  };

  return render();
};
